// Copyright (c) Docugami, Inc. All rights reserved.

import { createTheme, loadTheme } from "@fluentui/react";
import { dgFontFamily, primaryFontExpanded } from "components/common/FontConstants";
import { AppTheme, ThemeColors } from "../ThemeTypes";
import { dgIconNames } from "hooks/dgIcons";

/**
 * Docugami brand colors
 */
export const docugamiColors: ThemeColors = {
    // Primary brand colors - Docugami green
    primary: '#4BBF44',
    primaryDark: '#34A32E',
    primaryLight: '#B9F9AA',
    primaryAccent: '#4BBF44',

    // Action colors
    actionBlue: '#0085FF',
    lightActionBlue: '#0085FF80',

    // Semantic colors
    success: '#A3F090',
    warning: '#f2df30',
    error: '#FF3F3F',
    info: '#56CCF2',

    // Header colors
    headerBackground: '#4BBF44',
    headerText: '#FFFFFF',

    // UI colors
    backgroundWhite: '#F8FAFB',
    backgroundGray: '#EBEBEB',
    backgroundPanel: '#F3F3F3',
    landingPageBackground: '#F6F6F6',

    // Text colors
    textPrimary: '#58595B',
    textSecondary: '#999999',
    textDisabled: '#99999940',

    // Status colors
    confirmed: '#C6FF99',
    unconfirmed: '#FEFF997F',
    highlight: '#FEC705',

    // Component-specific colors
    chunkSelected: '#56CCF2BF',
    chunkHover: '#AAE6F980',
    machineGreen: '#B6EB82',
    humanBlue: '#3ED1FF',
};

const docugamiPrimaryThemeSettings = {
    defaultFontStyle: { fontFamily: primaryFontExpanded },
    palette: {
        themePrimary: docugamiColors.primaryAccent,
        themeLighterAlt: '#f9fdf9',
        themeLighter: '#e8f5e9',
        themeLight: '#d5edd6',
        themeTertiary: '#aedab0',
        themeSecondary: '#8dc990',
        themeDarkAlt: '#73ae76',
        themeDark: '#619364',
        themeDarker: '#486d49',
        neutralLighterAlt: '#faf9f8',
        neutralLighter: '#f3f2f1',
        neutralLight: '#edebe9',
        neutralQuaternaryAlt: '#e1dfdd',
        neutralQuaternary: '#d0d0d0',
        neutralTertiaryAlt: '#c8c6c4',
        neutralTertiary: '#a19f9d',
        neutralSecondary: '#605e5c',
        neutralPrimaryAlt: '#3b3a39',
        neutralPrimary: '#323130',
        neutralDark: '#201f1e',
        black: '#000000',
        white: '#ffffff',
    }
};

const docugamiSecondaryThemeSettings = {
    defaultFontStyle: { fontFamily: "'" + dgFontFamily + "', sans-serif" },
    palette: {
        themePrimary: docugamiColors.primaryAccent,
        themeLighterAlt: '#fafdfa',
        themeLighter: '#eaf7eb',
        themeLight: '#d9f0da',
        themeTertiary: '#b5e2b7',
        themeSecondary: '#97d49a',
        themeDarkAlt: '#3DB535',
        themeDark: '#699d6c',
        themeDarker: '#4d744f',
        neutralLighterAlt: '#eeeeee',
        neutralLighter: '#eaeaea',
        neutralLight: '#e1e1e1',
        neutralQuaternaryAlt: '#d1d1d1',
        neutralQuaternary: '#c8c8c8',
        neutralTertiaryAlt: '#c0c0c0',
        neutralTertiary: '#a19f9d',
        neutralSecondary: '#605e5c',
        neutralPrimaryAlt: '#3b3a39',
        neutralPrimary: '#323130',
        neutralDark: '#201f1e',
        black: '#000000',
        white: '#f6f6f6',
    }
};

const docugamiHeaderThemeSettings = {
    defaultFontStyle: { fontFamily: primaryFontExpanded },
    palette: {
        themePrimary: '#ffffff',
        themeLighterAlt: '#767676',
        themeLighter: '#a6a6a6',
        themeLight: '#c8c8c8',
        themeTertiary: '#d0d0d0',
        themeSecondary: '#dadada',
        themeDarkAlt: '#eaeaea',
        themeDark: '#f4f4f4',
        themeDarker: '#f8f8f8',
        neutralLighterAlt: '#96d586',
        neutralLighter: '#94d284',
        neutralLight: '#8ec97f',
        neutralQuaternaryAlt: '#84bc76',
        neutralQuaternary: '#7eb371',
        neutralTertiaryAlt: '#79ac6c',
        neutralTertiary: '#c8c8c8',
        neutralSecondary: '#d0d0d0',
        neutralPrimaryAlt: '#dadada',
        neutralPrimary: '#ffffff',
        neutralDark: '#f4f4f4',
        black: '#f8f8f8',
        white: docugamiColors.headerBackground,
    }
};

/**
 * Docugami theme configuration
 */
export const docugamiTheme: AppTheme = {
    id: 'docugami',
    name: 'Docugami',
    brand: {
        productName: 'Docugami',
        companyName: 'Docugami, Inc.',
        shortName: 'DG',
    },
    logo: {
        textLogoIcon: dgIconNames.DocugamiLogoNoMark.toString(),
        logoWithMarkIcon: dgIconNames.DocugamiLogo.toString(),
        faviconUrl: '/favicon.ico',
    },
    colors: docugamiColors,
    fluentPrimaryTheme: loadTheme(docugamiPrimaryThemeSettings),
    fluentSecondaryTheme: createTheme(docugamiSecondaryThemeSettings),
    fluentHeaderTheme: createTheme(docugamiHeaderThemeSettings),
};
